import React, { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, RotateCcw, Save, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

// Admin panel embedded in AdminSettings > tab "Piano". Configura i default
// che vengono applicati alla creazione di ogni nuovo piano economico.
const fmtEuro = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(n) || 0);
const uid = (p) => `${p}_${Math.random().toString(36).slice(2, 10)}`;

const RENO_KEYS = ["STRONG", "MEDIUM", "LIGHT"];
const PCT_OF_OPTIONS = [
  { value: "ricavi",     label: "Ricavi (vendita)" },
  { value: "proposta",   label: "Proposta (acquisto)" },
  { value: "manodopera", label: "Manodopera" },
  { value: "rendita",    label: "Rendita catastale" },
];
const RENO_DESC_HINT = {
  STRONG: "Rifacimento totale (impianti + infissi)",
  MEDIUM: "Si salva qualcosa fra impianti e infissi",
  LIGHT:  "Si salvano sia infissi sia impianti",
};

const NumCell = ({ value, onChange, testId, className = "", step = "0.01" }) => (
  <Input
    type="number"
    step={step}
    value={value ?? 0}
    onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
    className={`h-8 text-right font-mono-num text-xs px-2 ${className}`}
    data-testid={testId}
  />
);

const AdminPianoDefaults = () => {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/piano-defaults");
      // Assicura che ogni riga abbia un id client per il rendering
      const data = r.data || {};
      data.cost_lines = (data.cost_lines || []).map((l) => ({ ...l, _id: uid("cl") }));
      data.materials  = (data.materials  || []).map((m) => ({ ...m, _id: uid("mt"), quantita: m.quantita ?? 1 }));
      setForm(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento default");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      // Rimuovi _id client prima del send
      const payload = {
        imprevisti_pct: Number(form.imprevisti_pct) || 0,
        sostitutiva_pct: Number(form.sostitutiva_pct) || 0,
        pubblicita_pct: Number(form.pubblicita_pct) || 0,
        street_discount_pct: Number(form.street_discount_pct) || 0,
        cost_lines: (form.cost_lines || []).map(({ _id, ...rest }) => ({ // eslint-disable-line no-unused-vars
          label: rest.label || "",
          peggiore: Number(rest.peggiore) || 0,
          medio: Number(rest.medio) || 0,
          migliore: Number(rest.migliore) || 0,
          note: rest.note || "",
          mode: rest.mode === "percent" ? "percent" : "fixed",
          pct_of: rest.mode === "percent" ? (rest.pct_of || "") : "",
        })),
        materials: (form.materials || []).map(({ _id, ...rest }) => ({ // eslint-disable-line no-unused-vars
          label: rest.label || "",
          quantita: Number(rest.quantita) || 1,
          prezzo: Number(rest.prezzo) > 0 ? Number(rest.prezzo) : Number(rest.prezzo_a) || 0,
        })),
        reno_matrix: form.reno_matrix || {},
      };
      const r = await api.put("/admin/piano-defaults", payload);
      const data = r.data || {};
      data.cost_lines = (data.cost_lines || []).map((l) => ({ ...l, _id: uid("cl") }));
      data.materials  = (data.materials  || []).map((m) => ({ ...m, _id: uid("mt"), quantita: m.quantita ?? 1 }));
      setForm(data);
      toast.success("Default Piano Economico salvati");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Ripristinare i preset di sistema? Le voci personalizzate andranno perse.")) return;
    try {
      const r = await api.post("/admin/piano-defaults/reset");
      const data = r.data || {};
      data.cost_lines = (data.cost_lines || []).map((l) => ({ ...l, _id: uid("cl") }));
      data.materials  = (data.materials  || []).map((m) => ({ ...m, _id: uid("mt"), quantita: m.quantita ?? 1 }));
      setForm(data);
      toast.success("Preset di sistema ripristinati");
    } catch (e) {
      toast.error("Errore ripristino");
    }
  };

  const updCost = (_id, k, v) => setForm((f) => ({
    ...f, cost_lines: f.cost_lines.map((l) => l._id === _id ? {
      ...l,
      [k]: (k === "label" || k === "note" || k === "mode" || k === "pct_of") ? v : (Number(v) || 0),
    } : l),
  }));
  const addCost = () => setForm((f) => ({ ...f, cost_lines: [...(f.cost_lines || []), { _id: uid("cl"), label: "Nuova voce", peggiore: 0, medio: 0, migliore: 0, note: "", mode: "fixed", pct_of: "" }] }));
  const rmCost  = (_id) => setForm((f) => ({ ...f, cost_lines: f.cost_lines.filter((l) => l._id !== _id) }));

  const updMat = (_id, k, v) => setForm((f) => ({
    ...f, materials: f.materials.map((m) => m._id === _id ? { ...m, [k]: k === "label" ? v : (Number(v) || 0) } : m),
  }));
  const addMat = () => setForm((f) => ({ ...f, materials: [...(f.materials || []), { _id: uid("mt"), label: "Nuovo materiale", quantita: 1, prezzo: 0 }] }));
  const rmMat  = (_id) => setForm((f) => ({ ...f, materials: f.materials.filter((m) => m._id !== _id) }));

  const updReno = (tier, k, v) => setForm((f) => ({
    ...f,
    reno_matrix: {
      ...(f.reno_matrix || {}),
      [tier]: {
        ...(f.reno_matrix?.[tier] || {}),
        [k]: k === "desc" ? v : (Number(v) || 0),
      },
    },
  }));

  if (loading || !form) {
    return (
      <div className="flex items-center justify-center py-14 text-slate-500 text-sm" data-testid="piano-defaults-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento default Piano Economico…
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="piano-defaults-panel">
      {/* Percentuali globali */}
      <section>
        <h3 className="font-display font-bold text-lg tracking-tight mb-1">Percentuali di calcolo</h3>
        <p className="text-xs text-slate-500 mb-3">
          Applicate a tutti i nuovi piani. Ogni utente Pro può comunque aggiustarle sul singolo piano.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { key: "imprevisti_pct",      label: "Imprevisti % (su manodopera)" },
            { key: "sostitutiva_pct",     label: "Imposta sostitutiva %" },
            { key: "pubblicita_pct",      label: "Pubblicità % (su ricavi)" },
            { key: "street_discount_pct", label: "Street price sconto %" },
          ].map(({ key, label }) => (
            <div key={key}>
              <Label htmlFor={`pdef-${key}`} className="text-xs uppercase tracking-wider text-slate-600 font-semibold">{label}</Label>
              <Input
                id={`pdef-${key}`}
                type="number" step="0.1" min="0" max="100"
                value={form[key] ?? 0}
                onChange={(e) => upd(key, e.target.value === "" ? 0 : Number(e.target.value))}
                className="mt-1 rounded-xl bg-slate-100 border-0 focus-visible:ring-2 focus-visible:ring-[#007AFF] font-mono-num"
                data-testid={`pdef-${key}`}
              />
            </div>
          ))}
        </div>
      </section>

      {/* Voci di costo di default */}
      <section>
        <div className="flex items-center justify-between mb-1">
          <div>
            <h3 className="font-display font-bold text-lg tracking-tight">Voci di costo di default</h3>
            <p className="text-xs text-slate-500 mt-0.5">Vengono precaricate quando un utente Pro crea un nuovo piano.</p>
          </div>
          <Button variant="outline" size="sm" className="rounded-full" onClick={addCost} data-testid="pdef-cost-add">
            <Plus className="w-3 h-3 mr-1" /> Aggiungi voce
          </Button>
        </div>
        <div className="border rounded-lg overflow-hidden mt-3">
          <table className="w-full text-xs">
            <thead className="bg-slate-100 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-2 py-2">Voce</th>
                <th className="text-left px-2 py-2 w-16">Tipo</th>
                <th className="text-left px-2 py-2 w-40">Riferimento %</th>
                <th className="text-right px-2 py-2 w-24">Peggiore</th>
                <th className="text-right px-2 py-2 w-24">Medio</th>
                <th className="text-right px-2 py-2 w-24">Migliore</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {(form.cost_lines || []).map((l) => {
                const isPct = l.mode === "percent";
                return (
                  <tr key={l._id} className="border-t border-slate-100">
                    <td className="px-1 py-1"><Input value={l.label} onChange={(e) => updCost(l._id, "label", e.target.value)} className="h-8 text-xs" data-testid={`pdef-cost-label-${l._id}`} /></td>
                    <td className="px-1 py-1">
                      <Select value={l.mode || "fixed"} onValueChange={(v) => updCost(l._id, "mode", v)}>
                        <SelectTrigger className="h-8 text-xs px-2" data-testid={`pdef-cost-mode-${l._id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fixed">€</SelectItem>
                          <SelectItem value="percent">%</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-1 py-1">
                      {isPct ? (
                        <Select value={l.pct_of || ""} onValueChange={(v) => updCost(l._id, "pct_of", v)}>
                          <SelectTrigger className="h-8 text-xs px-2" data-testid={`pdef-cost-pctof-${l._id}`}>
                            <SelectValue placeholder="di…" />
                          </SelectTrigger>
                          <SelectContent>
                            {PCT_OF_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="text-[10px] text-slate-300 pl-2">—</span>
                      )}
                    </td>
                    <td className="px-1 py-1"><NumCell value={l.peggiore} onChange={(v) => updCost(l._id, "peggiore", v)} testId={`pdef-cost-p-${l._id}`} /></td>
                    <td className="px-1 py-1"><NumCell value={l.medio}    onChange={(v) => updCost(l._id, "medio", v)}    testId={`pdef-cost-m-${l._id}`} /></td>
                    <td className="px-1 py-1"><NumCell value={l.migliore} onChange={(v) => updCost(l._id, "migliore", v)} testId={`pdef-cost-g-${l._id}`} /></td>
                    <td className="px-1 py-1">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-rose-600" onClick={() => rmCost(l._id)} data-testid={`pdef-cost-del-${l._id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {(form.cost_lines || []).length === 0 && (
                <tr><td colSpan={7} className="text-center py-6 text-slate-400">Nessuna voce di costo. Aggiungi la prima.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Materiali di default (preventivo unico) */}
      <section>
        <div className="flex items-center justify-between mb-1">
          <div>
            <h3 className="font-display font-bold text-lg tracking-tight">Materiali di default</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Ogni riga ha <b>Quantità</b> e <b>€/unità</b>. Totale riga = quantità × prezzo unitario.
              Il totale materiali viene poi riportato in automatico nella scheda <b>Scenari &amp; Costi</b> del piano.
            </p>
          </div>
          <Button variant="outline" size="sm" className="rounded-full" onClick={addMat} data-testid="pdef-mat-add">
            <Plus className="w-3 h-3 mr-1" /> Aggiungi materiale
          </Button>
        </div>
        <div className="border rounded-lg overflow-hidden mt-3">
          <table className="w-full text-xs">
            <thead className="bg-slate-100 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-2 py-2">Materiale</th>
                <th className="text-right px-2 py-2 w-24">Qtà</th>
                <th className="text-right px-2 py-2 w-28">€/unità</th>
                <th className="text-right px-2 py-2 w-32">Totale</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {(form.materials || []).map((m) => {
                const prezzo = (Number(m.prezzo) > 0 ? Number(m.prezzo) : Number(m.prezzo_a)) || 0;
                const tot = (Number(m.quantita) || 0) * prezzo;
                return (
                  <tr key={m._id} className="border-t border-slate-100">
                    <td className="px-1 py-1"><Input value={m.label} onChange={(e) => updMat(m._id, "label", e.target.value)} className="h-8 text-xs" data-testid={`pdef-mat-label-${m._id}`} /></td>
                    <td className="px-1 py-1"><NumCell value={m.quantita} onChange={(v) => updMat(m._id, "quantita", v)} step="0.01" testId={`pdef-mat-qty-${m._id}`} /></td>
                    <td className="px-1 py-1"><NumCell value={prezzo} onChange={(v) => updMat(m._id, "prezzo", v)} testId={`pdef-mat-prezzo-${m._id}`} /></td>
                    <td className="px-2 py-1 text-right font-mono-num text-slate-800 font-semibold">{fmtEuro(tot)}</td>
                    <td className="px-1 py-1">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-rose-600" onClick={() => rmMat(m._id)} data-testid={`pdef-mat-del-${m._id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {(form.materials || []).length === 0 && (
                <tr><td colSpan={5} className="text-center py-6 text-slate-400">Nessun materiale. Aggiungi il primo.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Reno matrix */}
      <section>
        <h3 className="font-display font-bold text-lg tracking-tight mb-1">Tipologie ristrutturazione (€/m²)</h3>
        <p className="text-xs text-slate-500 mb-3">
          €/m² usati dal pulsante <b>Auto-fill manodopera</b> nell'editor del Piano. Un utente Pro sceglie una delle 3 tipologie e la manodopera per i 3 scenari viene compilata come <b>€/m² × m² immobile</b>.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {RENO_KEYS.map((tier) => {
            const t = form.reno_matrix?.[tier] || { peggiore: 0, medio: 0, migliore: 0, desc: "" };
            return (
              <div key={tier} className="border rounded-lg p-3 space-y-2 bg-slate-50/60" data-testid={`pdef-reno-${tier}`}>
                <div className="font-bold text-sm text-slate-800 uppercase tracking-wider">{tier}</div>
                <Input
                  value={t.desc || ""}
                  onChange={(e) => updReno(tier, "desc", e.target.value)}
                  placeholder={RENO_DESC_HINT[tier]}
                  className="h-8 text-xs bg-white"
                  data-testid={`pdef-reno-${tier}-desc`}
                />
                <div className="grid grid-cols-3 gap-2 pt-1">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-rose-600">Peggiore</Label>
                    <NumCell value={t.peggiore} onChange={(v) => updReno(tier, "peggiore", v)} testId={`pdef-reno-${tier}-p`} />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-amber-600">Medio</Label>
                    <NumCell value={t.medio} onChange={(v) => updReno(tier, "medio", v)} testId={`pdef-reno-${tier}-m`} />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-emerald-600">Migliore</Label>
                    <NumCell value={t.migliore} onChange={(v) => updReno(tier, "migliore", v)} testId={`pdef-reno-${tier}-g`} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <div className="flex items-center justify-between border-t border-slate-100 pt-4">
        <p className="text-[11px] text-slate-500">
          Modifiche a questi default si applicano ai <b>nuovi piani creati d'ora in poi</b>. I piani già esistenti mantengono i loro valori attuali.
        </p>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={reset} className="rounded-full h-10" data-testid="pdef-reset">
            <RotateCcw className="w-4 h-4 mr-2" /> Ripristina preset
          </Button>
          <Button onClick={save} disabled={saving} className="rounded-full h-10 bg-[#007AFF] hover:bg-[#0060df]" data-testid="pdef-save">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvo…</> : <><Save className="w-4 h-4 mr-2" /> Salva default</>}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminPianoDefaults;
